import type { ITaskAllocation } from "@/pages/ForecastingPage";

/**
 * A task can be allocated to at most one week, so a task is identified by where
 * it comes from — not by the week it currently sits in. Re-keying a moved task
 * therefore replaces its previous week's entry automatically.
 *
 * The same format identifies a selected task (`AllocateTasksStep`'s
 * multi-select), a still-valid task (`ForecastingPage`'s pruning) and a
 * carried-forward task, so every one of those lookups shares this helper.
 *
 * The forecasting flow can carry 100k+ tasks, and each of those lookups would
 * otherwise rebuild this string per task per render — so the key is computed
 * once and cached on the object as `allocationKey`. Call this only when
 * creating a task/allocation object or converting from a foreign shape; read
 * `allocationKey` everywhere else.
 */
export const getTaskAllocationKey = ({
  taskId,
  stageId,
  plotNumber
}: {
  taskId: string;
  stageId: string;
  plotNumber: string;
}) => `${taskId}-${stageId}-${plotNumber}`;

/**
 * Allocations grouped by the week they sit in — the shape `AllocateTasksStep`
 * renders, and the one `ForecastingPage` parks in a ref between visits to that
 * step. Keyed the same way the week columns are, so a drop can replace only the
 * weeks it touched and leave every other week's array identical.
 *
 * A week is present only while it holds tasks: {@link withWeekTasks} deletes an
 * emptied week, so `map.size > 0` means "something is allocated somewhere".
 */
export type WeeklyAllocations = Map<string, ITaskAllocation[]>;

/** Fans a flat list of allocations out into week columns. */
export const groupAllocationsByWeek = (
  allocations: Iterable<ITaskAllocation>
): WeeklyAllocations => {
  const byWeek: WeeklyAllocations = new Map();
  for (const allocation of allocations) {
    const weekTasks = byWeek.get(allocation.monthWeek);
    if (weekTasks) {
      weekTasks.push(allocation);
    } else {
      byWeek.set(allocation.monthWeek, [allocation]);
    }
  }
  return byWeek;
};

/** Flattens week columns back into one list, for the steps that follow. */
export const flattenAllocations = (
  byWeek: WeeklyAllocations
): ITaskAllocation[] => {
  const allocations: ITaskAllocation[] = [];
  byWeek.forEach((weekTasks) => allocations.push(...weekTasks));
  return allocations;
};

/**
 * Copy of `byWeek` with one week's tasks replaced. An emptied week is dropped
 * from the map so it falls back to the shared `NO_ALLOCATED_TASKS` constant.
 *
 * Every other week keeps its exact array — that is what lets `WeekDropZone`
 * stay memoized while a neighbouring week is being filled.
 */
export const withWeekTasks = (
  byWeek: WeeklyAllocations,
  monthWeek: string,
  tasks: ITaskAllocation[]
): WeeklyAllocations => {
  const next = new Map(byWeek);
  if (tasks.length > 0) {
    next.set(monthWeek, tasks);
  } else {
    next.delete(monthWeek);
  }
  return next;
};

/**
 * Drops every allocation whose task is no longer available — used when the
 * plot/stage selection changes under the wizard. Returns `byWeek` untouched
 * when nothing was pruned, so callers can skip a needless state update.
 */
export const pruneAllocations = (
  byWeek: WeeklyAllocations,
  validKeys: Set<string>
): WeeklyAllocations => {
  const next: WeeklyAllocations = new Map();
  let pruned = false;

  byWeek.forEach((weekTasks, monthWeek) => {
    const kept = weekTasks.filter((task) => validKeys.has(task.allocationKey));
    if (kept.length !== weekTasks.length) pruned = true;
    if (kept.length > 0) next.set(monthWeek, kept);
  });

  return pruned ? next : byWeek;
};
